/**
 * ProductCard Component - Displays a single product with image, details, and wishlist button
 */

import { Box, Inline, Stack, Strong } from 'braid-design-system';
import type React from 'react';
import { useState } from 'react';
import { Link } from 'react-router';

import { useWishlist } from '../../context/WishlistContext';
import type { Product } from '../../types';

import * as styles from './ProductCard.css';

interface ProductCardProps {
  product: Product;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const inWishlist = isInWishlist(product.id);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const hasMultipleImages = product.images.length > 1;
  const productPath = `/products/${product.id}`;

  const getSilhouetteVariant = (id: string) => {
    const hash = id
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const variants = styles.imageContainerVariants;
    return variants[hash % variants.length];
  };

  const silhouetteVariant = getSilhouetteVariant(product.id);

  const handleWishlistToggle = () => {
    if (inWishlist) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };

  return (
    <Box component="article">
      <div className={styles.imageContainer[silhouetteVariant]}>
        {(product.featured || product.isNew) && (
          <div className={styles.badgeContainer}>
            {product.featured && (
              <span className={styles.featuredBadge}>Featured</span>
            )}
            {product.isNew && <span className={styles.newBadge}>New</span>}
          </div>
        )}
        <Link to={productPath} className={styles.imageLink}>
          <img
            src={product.images[activeImageIndex]}
            alt={product.name}
            className={styles.image}
            loading="lazy"
          />
        </Link>
        {hasMultipleImages && (
          <div className={styles.carouselDots} data-carousel-dots>
            {product.images.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`${styles.carouselDot} ${
                  index === activeImageIndex ? styles.carouselDotActive : ''
                }`}
                onMouseEnter={() => setActiveImageIndex(index)}
                onClick={() => setActiveImageIndex(index)}
                aria-label={`View image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
      <Box paddingY="small">
        <Stack space="medium" align="left">
          <Stack space="xsmall" align="left">
            <h2>
              <Link to={productPath} className={styles.productLink}>
                <Strong>{product.name}</Strong>
              </Link>
            </h2>
            <Link
              to={`/products?category=${encodeURIComponent(product.category)}`}
            >
              <p className={styles.category}>{product.category}</p>
            </Link>
            <p>{product.description}</p>
          </Stack>
          <Inline space="none">
            <button
              type="button"
              className={styles.wishlistButton}
              onClick={handleWishlistToggle}
            >
              {inWishlist ? '♥ Saved' : '♡ Add to Wishlist'}
            </button>
          </Inline>
        </Stack>
      </Box>
    </Box>
  );
};
